import ApplicationSelector from 'components/Application/ApplicationSelector';
import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import BACKEND_URL from 'constants/backendUrl';
import Application from 'core/models/application';
import { useInvalidateApplications } from 'core/api/applications';
import { useModalStore } from 'stores/modal';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { MagnifyingGlass } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';
import { POST } from 'utils/http';
import { SFileInputContainer, SFileInputTitle, SImportLoading, SPasteTextarea } from '../ContactsForm/styles';

export type ImportedApplication = {
    company: string;
    job: string;
    source: string;
    place?: string;
    salary?: string;
    reference?: string;
    dateOfPublication?: string;
    description?: string;
    resources?: string;
};

type Inputs = {
    files: FileList;
    csvText?: string;
};

type ImportApplicationsResponse = {
    importedApplications: ImportedApplication[];
};

type CreateApplicationsResponse = {
    createdApplications: Application[];
};

enum ImportStatus {
    CREATING = 'CREATING',
    DONE = 'DONE',
    LOADING = 'LOADING',
    READY = 'READY',
}

const stripMarkdown = (csv: string): string =>
    csv
        .replace(/\[([^\]\n]+)\]\([^)\n]*\)/g, '$1')
        .replace(/\*\*([^*\n]+)\*\*/g, '$1')
        .replace(/\*([^*\n]+)\*/g, '$1')
        .replace(/`([^`\n]+)`/g, '$1');

const importApplicationsRequest = async (formData: FormData): Promise<ImportApplicationsResponse> =>
    (await POST<ImportApplicationsResponse>(
        `${BACKEND_URL}/api/applications/import`,
        formData,
    )) as ImportApplicationsResponse;

const createApplicationsRequest = async (applications: ImportedApplication[]): Promise<CreateApplicationsResponse> =>
    (await POST<CreateApplicationsResponse>(`${BACKEND_URL}/api/applications`, {
        applications,
    })) as CreateApplicationsResponse;

type ApplicationsFormProps = {
    closeModal(): void;
    pasteMode?: boolean;
};

const ApplicationsForm: FC<ApplicationsFormProps> = ({ closeModal, pasteMode = false }) => {
    const invalidateApplications = useInvalidateApplications();
    const closeModalStore = useModalStore((s) => s.closeModal);
    const theme = useTheme();
    const { t } = useTranslation();

    const [importedApplications, setImportedApplications] = useState<ImportedApplication[]>([]);
    const [importStatus, setImportStatus] = useState<ImportStatus>(ImportStatus.READY);

    const methods = useForm<Inputs>();
    const { handleSubmit, register } = methods;

    const processImport = async (blob: Blob, fileName: string) => {
        try {
            const formData = new FormData();
            formData.append('file', blob, fileName);
            setImportStatus(ImportStatus.LOADING);
            const response = await importApplicationsRequest(formData);
            await new Promise((resolve) => setTimeout(resolve, 500));
            setImportedApplications(response.importedApplications);
            setImportStatus(ImportStatus.DONE);
        } catch {
            toast.error(t('application.import.error'));
            setImportStatus(ImportStatus.READY);
        }
    };

    const onSubmitImport = handleSubmit(async (values: Inputs) => {
        const file = values.files?.[0];

        if (file) {
            await processImport(file, file.name);
        } else if (values.csvText?.trim()) {
            const blob = new Blob([stripMarkdown(values.csvText)], { type: 'text/csv;charset=utf-8;' });
            await processImport(blob, 'applications.csv');
        } else {
            toast.error(t('application.import.error'));
        }
    });

    const onSubmitCreate = async (selectedApplications: ImportedApplication[]) => {
        try {
            setImportStatus(ImportStatus.CREATING);
            await createApplicationsRequest(selectedApplications);
            invalidateApplications();
            toast.success(t('application.import.success', { count: selectedApplications.length }));
            closeModalStore();
        } catch {
            toast.error(t('application.import.error'));
            closeModalStore();
        }
    };

    switch (importStatus) {
        case ImportStatus.DONE:
            return (
                <ApplicationSelector
                    cancel={() => closeModalStore()}
                    selectableApplications={importedApplications}
                    submit={onSubmitCreate}
                />
            );
        case ImportStatus.CREATING:
            return (
                <SImportLoading>
                    <MagnifyingGlass
                        ariaLabel="MagnifyingGlass-loading"
                        color={theme.color.secondary.main}
                        height="80"
                        width="80"
                        visible
                    />
                    <Trans i18nKey="application.import.creating" />
                </SImportLoading>
            );
        case ImportStatus.LOADING:
            return (
                <SImportLoading>
                    <MagnifyingGlass
                        ariaLabel="MagnifyingGlass-loading"
                        color={theme.color.secondary.main}
                        height="80"
                        width="80"
                        visible
                    />
                    <Trans i18nKey="application.import.loading" />
                </SImportLoading>
            );
        default:
            return (
                <FormProvider {...methods}>
                    <form onSubmit={onSubmitImport}>
                        {pasteMode ? (
                            <>
                                <SFileInputTitle>{t('application.import.paste-label')}</SFileInputTitle>
                                <SPasteTextarea
                                    {...register('csvText')}
                                    placeholder={t('application.import.paste-placeholder')}
                                    rows={8}
                                />
                                <SFileInputTitle>{t('application.import.file-title')}</SFileInputTitle>
                                <SFileInputContainer>
                                    <input {...register('files')} accept=".csv" type="file" />
                                </SFileInputContainer>
                            </>
                        ) : (
                            <>
                                <SFileInputTitle>{t('application.import.file-title')}</SFileInputTitle>
                                <SFileInputContainer>
                                    <input
                                        {...register('files', {
                                            required: t('common.required-field') as string,
                                        })}
                                        accept=".csv"
                                        type="file"
                                    />
                                </SFileInputContainer>
                            </>
                        )}
                        <BaseModal.BottomActions>
                            <Button onClick={() => closeModal()} variant="text">
                                {t('common.cancel')}
                            </Button>
                            <Button submit>{t('application.import.label')}</Button>
                        </BaseModal.BottomActions>
                    </form>
                </FormProvider>
            );
    }
};

export default ApplicationsForm;
